import { useSyncExternalStore } from 'react';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

function createAuthStore(persistent: boolean) {
  let token: string | null = persistent ? localStorage.getItem(ACCESS_TOKEN_KEY) : null;
  let refreshToken: string | null = persistent ? localStorage.getItem(REFRESH_TOKEN_KEY) : null;
  const listeners = new Set<() => void>();
  const logoutListeners = new Set<() => void>();

  const notify = () => {
    listeners.forEach((l) => {
      l();
    });
  };

  return {
    getToken: (): string | null => token,
    getRefreshToken: (): string | null => refreshToken,
    setAccessToken: (newToken: string) => {
      token = newToken;
      if (persistent) localStorage.setItem(ACCESS_TOKEN_KEY, newToken);
      notify();
    },
    setAuthTokens: (newToken: string, newRefreshToken: string) => {
      token = newToken;
      refreshToken = newRefreshToken;
      if (persistent) {
        localStorage.setItem(ACCESS_TOKEN_KEY, newToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
      }
      notify();
    },
    logout: () => {
      token = null;
      refreshToken = null;
      if (persistent) {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
      }
      logoutListeners.forEach((listener) => {
        listener();
      });
      notify();
    },
    registerLogoutListener: (listener: () => void): (() => void) => {
      logoutListeners.add(listener);
      return () => logoutListeners.delete(listener);
    },
    subscribe: (listener: () => void): (() => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// Embedded mode is entered via the /external-auth route — tokens are received from
// the Intacct iframe and must not be persisted to localStorage.
const isEmbedded = window.location.pathname.split('/').find(Boolean) === 'external-auth';
export const authStore = createAuthStore(!isEmbedded);

export function useToken(): string | null {
  return useSyncExternalStore(authStore.subscribe, authStore.getToken);
}

export function useRefreshToken(): string | null {
  return useSyncExternalStore(authStore.subscribe, authStore.getRefreshToken);
}
